import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import { abyssColors } from '@/theme/colors';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { feedbackContainerStyles } from './styles';

type DetailsGridProps = {
  title: string;
  data: [string, string | number][];
  getFieldName?: (key: string) => string;
  getFormattedValue?: (key: string, value: string) => React.ReactNode;
  displayableFields: string[];
};

export const DetailsGrid = ({
  title,
  data,
  getFieldName,
  getFormattedValue,
  displayableFields,
}: DetailsGridProps) => {
  const filteredData = data.filter(([key]) => displayableFields?.includes(key));

  if (data.length === 0) {
    return (
      <Box sx={feedbackContainerStyles} data-testid="feedback-circle">
        <FeedbackCircle
          backgroundColor="none"
          text="No data available to show"
          textColor={abyssColors.primary[300]}
          icon={<AbyssIcon iconName="fallback-component-overview" size="2xl" />}
        />
      </Box>
    );
  }

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Typography variant="h5" sx={{ fontWeight: 500, fontSize: '1.2rem' }}>
        {title}
      </Typography>
      <Grid
        container
        direction="column"
        sx={{
          border: `0.1rem solid ${abyssColors.primary[100]}`,
          borderRadius: '0.4rem',
        }}
      >
        {filteredData.map(([key, value], index) => (
          <Grid
            container
            key={`${key}-${index}`}
            sx={{
              height: '4rem',
              borderBottom:
                index !== filteredData.length - 1
                  ? `0.1rem solid ${abyssColors.primary[100]}`
                  : 'none',
              '&:last-of-type': {
                borderBottom: `0.1rem solid ${abyssColors.primary[100]}`,
              },
            }}
          >
            <Grid
              size={{ xs: 6 }}
              sx={{
                px: '0.8rem',
                py: '0.8rem',
                borderRight: `0.1rem solid ${abyssColors.primary[100]}`,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="body1" fontWeight={400} fontSize="1.2rem">
                {getFieldName ? getFieldName(key) : key}
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 6 }}
              sx={{
                px: '0.8rem',
                py: '0.8rem',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {getFormattedValue ? getFormattedValue(key, value?.toString()) : value?.toString()}
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
